import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { useMarketHistory } from "@/hooks/useMarketHistory";
import { useIsOnline } from "@/hooks/useIsOnline";
import type { MarketRow } from "@/hooks/useMarketData";

interface MarketStripProps {
  row: MarketRow;
  /** Consider the quote stale if fetchedAt is older than this many ms. */
  staleAfterMs?: number;
  className?: string;
}

const DEFAULT_STALE_MS = 5 * 60 * 1000; // 5 minutes

function formatPrice(ticker: string, price: number) {
  // FX pairs, and any small-magnitude quote (|price| < 10, e.g. sub-$10 ETFs / low FX crosses),
  // price to 4dp; everything else 2dp (matches PartnerDashboard logic).
  // IG FX codes look like CS.D.GBPUSD.TODAY.IP; generic FX tickers are prefixed FX:.
  const fx = /CS\.D\.|FX:/.test(ticker);
  return price.toFixed(fx || Math.abs(price) < 10 ? 4 : 2);
}

function asAt(fetchedAt: string | null): string {
  if (!fetchedAt) return "—";
  const d = new Date(fetchedAt);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Build an SVG polyline points string from close history, normalised to the box. */
function buildSparkline(closes: number[], width: number, height: number): string {
  if (closes.length < 2) return "";
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const span = max - min || 1;
  const stepX = width / (closes.length - 1);
  return closes
    .map((c, i) => {
      const x = i * stepX;
      const y = height - ((c - min) / span) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

export function MarketStrip({ row, staleAfterMs = DEFAULT_STALE_MS, className }: MarketStripProps) {
  const { points, fromCache: sparkFromCache } = useMarketHistory(row.ticker, 30);
  const online = useIsOnline();

  const up = row.changePercent >= 0;
  const sparkColor = up ? "hsl(142 71% 45%)" : "hsl(0 72% 51%)"; // green / red

  const isOpen = useMemo(() => {
    const s = (row.marketState ?? "").toUpperCase();
    if (!s) return null;
    return s === "TRADEABLE" || s === "OPEN";
  }, [row.marketState]);

  const isQuoteStale = useMemo(() => {
    if (!row.fetchedAt) return true;
    const t = new Date(row.fetchedAt).getTime();
    if (Number.isNaN(t)) return true;
    return Date.now() - t > staleAfterMs;
  }, [row.fetchedAt, staleAfterMs]);

  // Offline: the quote can't be refreshed and the sparkline is served from cache, so the whole strip
  // is showing last-known (saved) data. Treat that as stale and label it explicitly.
  const showStale = isQuoteStale || !online || sparkFromCache;
  const staleLabel = !online ? "offline" : "stale";
  const staleTitle = !online
    ? "Offline — showing last saved quote"
    : "Quote may be out of date";

  const sparkPoints = useMemo(
    () => buildSparkline(points.map((p) => p.close), 64, 20),
    [points],
  );

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2",
        className,
      )}
    >
      {/* Label + open/closed dot */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              "inline-block h-2 w-2 shrink-0 rounded-full",
              isOpen === null
                ? "bg-muted-foreground/40"
                : isOpen
                  ? "bg-green-500"
                  : "bg-muted-foreground/40",
            )}
            title={isOpen === null ? "Market state unknown" : isOpen ? "Open" : "Closed"}
            aria-label={isOpen === null ? "Market state unknown" : isOpen ? "Market open" : "Market closed"}
          />
          <span className="truncate text-sm font-medium" title={row.name}>
            {row.name}
          </span>
          {showStale && (
            <span
              className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              title={staleTitle}
            >
              {staleLabel}
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">as at {asAt(row.fetchedAt)}</div>
      </div>

      {/* Sparkline */}
      <svg
        width={64}
        height={20}
        viewBox="0 0 64 20"
        className="shrink-0"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {sparkPoints && (
          <polyline
            points={sparkPoints}
            fill="none"
            stroke={sparkColor}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>

      {/* Price + day/7d % */}
      <div className="shrink-0 text-right">
        <div className="font-mono text-sm tabular-nums">{formatPrice(row.ticker, row.price)}</div>
        <div className="flex items-center justify-end gap-2">
          <span
            className={cn("font-mono text-xs tabular-nums", up ? "text-green-600" : "text-red-600")}
            title="Day change"
          >
            {up ? "+" : ""}
            {row.changePercent.toFixed(2)}%
            <span className="ml-0.5 text-muted-foreground">d</span>
          </span>
          <span
            className={cn(
              "font-mono text-xs tabular-nums",
              row.change7d == null ? "text-muted-foreground" : row.change7d >= 0 ? "text-green-600" : "text-red-600",
            )}
            title="7-day change"
          >
            {row.change7d == null ? "—" : `${row.change7d >= 0 ? "+" : ""}${row.change7d.toFixed(2)}%`}
            <span className="ml-0.5 text-muted-foreground">7d</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default MarketStrip;
